<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { getContext } from "svelte";
  import { TRANSITION_DURATION } from "$lib/utils/config.js";

  const { data, xScale, yScale, xRange, yGet } = getContext("LayerCake");

  const STROKE_WIDTH = 2;

  let {
    groups = [],
    colors = [],
    overlapFraction = 0.25,
    separatorEffect = "none",
    strokeColor = null,
    liftOnHover = false,
    activeGroup = undefined,
    activeIndex = undefined,
    activeEl = $bindable(null),
    maxBarHeight = 60,
    onhover = () => {},
    onleave = () => {},
  } = $props();

  let rawBarHeight = $derived(
    $yScale.bandwidth() / (1 + (groups.length - 1) * (1 - overlapFraction)) || 0
  );
  let barHeight = $derived(Math.min(rawBarHeight, maxBarHeight));
  let stepOffset = $derived(barHeight * (1 - overlapFraction) || 0);
  let groupHeight = $derived(barHeight + (groups.length - 1) * stepOffset);
  let bandwidthOffset = $derived(($yScale.bandwidth() - groupHeight) / 2);
</script>

<defs>
  <filter id="hbar-shadow" x="-10%" y="-10%" width="120%" height="120%">
    <feDropShadow dx="0" dy="-1" stdDeviation="1" flood-color="black" flood-opacity="0.12" />
  </filter>
  <clipPath id="clip-right-stroke">
    <rect x="-9999" y="-9999" width={$xRange[1] + 9999 - STROKE_WIDTH} height="99999" />
  </clipPath>
</defs>
<g class="grouped-bar-group" style="--transition-duration: {TRANSITION_DURATION}ms">
  {#each $data as d, di}
    {@const yPos = $yGet(d)}
    {@const y0 = Array.isArray(yPos) ? yPos[0] : yPos}
    {#each groups as group, gi}
      {@const val = d[group]}
      {#if val}
        {@const xPos = $xScale(val)}
        {@const barWidth = Math.max(0, xPos - $xScale(0))}
        {@const by = y0 + bandwidthOffset + gi * stepOffset}
        <rect
          class="bar"
          x={$xScale(0)}
          y={by}
          width={barWidth}
          height={barHeight}
          fill={colors[gi]}
          opacity={activeIndex != null ? (activeIndex === di && activeGroup === gi ? 1 : 0.4) : 1}
          filter={separatorEffect === "shadow" && gi > 0 ? "url(#hbar-shadow)" : undefined}
          onmousemove={(e) => { activeEl = e.currentTarget; onhover(e, d, group, gi, di); }}
          onmouseleave={() => { activeEl = null; onleave(); }}
          role="img"
        />
        {#if separatorEffect === "stroke" && strokeColor}
          <rect
            class="bar"
            x={$xScale(0)}
            y={by}
            width={barWidth}
            height={barHeight}
            fill="none"
            stroke={strokeColor}
            stroke-width={STROKE_WIDTH}
            clip-path="url(#clip-right-stroke)"
            opacity={activeIndex != null ? (activeIndex === di && activeGroup === gi ? 1 : 0.4) : 1}
            style="pointer-events: none;"
          />
        {/if}
      {:else}
        {@const by = y0 + bandwidthOffset + gi * stepOffset}
        {@const nullWidth = $xRange[1] * 0.4}
        <rect
          x={$xScale(0)}
          y={by}
          width={nullWidth}
          height={barHeight}
          fill="transparent"
          onmousemove={(e) => { activeEl = e.currentTarget; onhover(e, d, group, gi, di); }}
          onmouseleave={() => { activeEl = null; onleave(); }}
          role="img"
        />
      {/if}
    {/each}
  {/each}
</g>
{#if liftOnHover && activeIndex != null && activeGroup != null}
  {@const d = $data[activeIndex]}
  {@const yPos = $yGet(d)}
  {@const y0 = Array.isArray(yPos) ? yPos[0] : yPos}
  {@const group = groups[activeGroup]}
  {@const val = d[group]}
  {#if val != null}
    {@const xPos = $xScale(val)}
    {@const barWidth = Math.max(0, xPos - $xScale(0))}
    {@const by = y0 + bandwidthOffset + activeGroup * stepOffset}
    <g style="pointer-events: none;">
      <rect
        x={$xScale(0)}
        y={by}
        width={barWidth}
        height={barHeight}
        fill={colors[activeGroup]}
      />
      {#if strokeColor}
        <rect
          x={$xScale(0)}
          y={by}
          width={barWidth}
          height={barHeight}
          fill="none"
          stroke={strokeColor}
          stroke-width={STROKE_WIDTH}
          clip-path="url(#clip-right-stroke)"
        />
      {/if}
    </g>
  {/if}
{/if}

<style>
  .bar {
    transition: x var(--transition-duration) ease, width var(--transition-duration) ease, opacity 0.2s ease;
  }
</style>
